<template>
  <div v-if="showCoordinates" class="bg-white border-round mapToolbox mb-2" style="width: 220px; height: inherit">
    <div class="p-2">
      <div class="flex flex-row justify-content-between my-1">
        <strong>WGS</strong>
        <a v-if="coordinatesDirty" href="javascript:return 0" @click.prevent="coordinatesChanged">
          <i class="pi pi-check" />
        </a>
      </div>

      <div class="flex flex-row justify-content-between gap-2">
        <label for="coordinates-lat" class="align-self-center">Lat:</label>
        <InputText id="coordinates-lat" v-model="coordinates.lat" class="map-coordinates-input p-inputtext-sm"
          @change="coordinatesDirty = true" />
      </div>
      <div class="flex flex-row justify-content-between gap-2">
        <label for="coordinates-lng" class="align-self-center">Lng:</label>
        <InputText id="coordinates-lng" v-model="coordinates.lng" class="map-coordinates-input p-inputtext-sm"
          @change="coordinatesDirty = true" />
      </div>

      <div class="flex flex-row justify-content-between my-2">
        <strong>{{ projectionConversion?.properties ? projectionConversion.properties.alias : '' }}</strong>
        <a v-if="projectedDirty" href="javascript:return 0" @click.prevent="projectionChanged()">
          <i class="pi pi-check" />
        </a>
      </div>

      <div class="flex flex-row justify-content-between gap-2">
        <label for="projected-x" class="align-self-center">X:</label>
        <InputText id="projected-x" v-model="projected.x" class="map-coordinates-input p-inputtext-sm"
          @change="projectedDirty = true" />
      </div>

      <div class="flex flex-row justify-content-between gap-2">
        <label for="projected-y" class="align-self-center">Y:</label>
        <InputText id="projected-y" v-model="projected.y" class="map-coordinates-input p-inputtext-sm"
          @change="projectedDirty = true" />
      </div>

      <div class="flex flex-row justify-content-between gap-2 mt-3 mb-1">
      </div>
      <div v-if="elevation" class="flex flex-row justify-content-between mt-2 gap-2">
        <strong>{{ $t('Altitude') }}:</strong> {{ elevation }} m
      </div>
    </div>
  </div>
  <div class="bg-white border-round mapToolbox p-buttonset p-buttonset-vertical" style="width: 38px">
    <Button v-tooltip.right="$t('Coordinates')"
      :class="{ 'p-button-secondary': !showCoordinates, 'p-button-primary': showCoordinates }" @click="toggle()">
      <img src="/icons/toolbox/coordinates.svg"
        :style="{ filter: showCoordinates ? 'invert(100%)' : 'grayscale(100%)' }" style="width: 18px; height: 18px" />
    </Button>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Button from 'primevue/button';
import { MapController } from '../../../js/Map/map-controller';
import proj4 from 'proj4';
import { zoomLevelScale } from '../../../js/utils.js';
import { GeometryUtil } from '@/js/Map/geometry';

export default defineComponent({
  name: 'MapCoordinatesControl',
  components: { Button },
  props: {
    mapController: {
      type: Object as PropType<MapController>,
      required: true,
    },
  },

  methods: {
    coordinatesChanged(e) {
      if (isNaN(this.coordinates.lng) || isNaN(this.coordinates.lat)) {
        this.mapController.configuration.toastProvider({
          severity: 'warn',
          summary: this.mapController.configuration.translatorProvider('Invalid coordinates'),
          detail: this.mapController.configuration.translatorProvider('Coordinates must be valid numbers'),
        });
        return;
      }
      this.mapController.mapInstance.flyTo({
        center: {
          lng: parseFloat(this.coordinates.lng),
          lat: parseFloat(this.coordinates.lat),
        },
        maxDuration: 300,
      });
    },

    projectionChanged() {
      this.isUpdateProjection = false;
      if (isNaN(this.projected.x) || isNaN(this.projected.y)) {
        this.mapController.configuration.toastProvider({
          severity: 'warn',
          summary: this.mapController.configuration.translatorProvider('Invalid coordinates'),
          detail: this.mapController.configuration.translatorProvider('Coordinates must be valid numbers'),
        });
        return;
      }

      let center = GeometryUtil.projectCoordinates([parseFloat(this.projected.x), parseFloat(this.projected.y)], this.projectionConversion.description, "EPSG:4326");

      this.mapController.mapInstance.flyTo({
        center: {
          lng: center[0],
          lat: center[1],
        },
        maxDuration: 300,
      });
    },

    zoomLevelScale() {
      return zoomLevelScale;
    },

    toggle() {
      this.showCoordinates = !this.showCoordinates;
      if (this.showCoordinates) {
        this.setupMapViewListeners();
      } else {
        this.unSetupMapViewListeners();
      }
    },

    setupMapViewListeners() {
      this.updateCenter();

      this.mapController.mapInstance.on('move', this.updateCenter);
    },

    unSetupMapViewListeners() {
      this.mapController.mapInstance.off('move', this.updateCenter);
    },

    updateCenter() {
      this.coordinates = this.mapController.mapInstance.getCenter();
      this.coordinatesDirty = false;
      if (this.mapController.hasElevation()) {
        this.elevation = this.mapController.getCenterElevation();
      } else {
        this.elevation = undefined;
      }
      
      if (this.isUpdateProjection) {
        this.generateProjectedCenter();
      } else {
        this.isUpdateProjection = true;
      }
      this.$forceUpdate();
    },

    generateProjectedCenter() {
      if (!this.projectionConversion) {
        return;
      }
      let result = GeometryUtil.projectCoordinates([this.coordinates.lng, this.coordinates.lat], "EPSG:4326", this.projectionConversion.description);

      if (result) {
        this.projected.x = result[0];
        this.projected.y = result[1];
        this.projectedDirty = false;
      }
    },
  },
  data() {
    return {
      projectionConversion: this.mapController.configuration.projection,
      showCoordinates: false,
      coordinates: { lng: 0, lat: 0 },
      coordinatesDirty: false,
      projected: { x: 0, y: 0 },
      projectedDirty: false,
      elevation: undefined,
      isUpdateProjection: true
    };
  },
});
</script>
<style lang="scss">
.map-coordinates-input.p-inputtext-sm {
  border: 0 !important;
  border-radius: 0 !important;
  font-size: small !important;
  width: 170px;
}

html.dark {
  .map-coordinates-input.p-inputtext-sm {
    background-color: rgb(37, 38, 54);
  }
}
</style>
